import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaGuitar, 
  FaSearch, 
  FaFilter,
  FaSortAmountDown,
  FaTimes,
  FaStar,
  FaEye,
  FaSpinner,
  FaTags,
  FaPercent,
  FaCheck,
  FaBolt,
  FaBox,
  FaBars,
  FaArrowLeft,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../firebase";

// İndirim hesaplama fonksiyonu
const calculateDiscount = (guitar) => {
  if (guitar.originalPrice && guitar.price) {
    const original = parseFloat(guitar.originalPrice);
    const current = parseFloat(guitar.price);
    if (original > current) {
      return Math.round(((original - current) / original) * 100);
    }
  }
  return 0;
};

// Gitar kartı component'i
const GuitarCard = ({ guitar }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const discount = useMemo(() => calculateDiscount(guitar), [guitar]);
  const stock = guitar.stock || 0;
  const mainImage = guitar.imageUrls?.[0] || null;
  const tags = guitar.tags || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="group cursor-pointer"
      onClick={() => navigate(`/gitar/${guitar.id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden hover:border-blue-500/30 transition-all duration-300 shadow-lg hover:shadow-xl">
        {/* Resim */}
        <div className="relative h-100 md:h-100 overflow-hidden">
          {mainImage ? (
            <img
              src={mainImage}
              alt={guitar.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
              <FaGuitar className="text-gray-700 text-4xl" />
            </div>
          )}
          
          {/* Badgeler */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-20">
            {discount > 0 && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1"
              >
                <FaPercent size={10} />
                %{discount} İNDİRİM
              </motion.span>
            )}
            {guitar.featured && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
              >
                <FaStar size={10} />
                ÖNE ÇIKAN
              </motion.span>
            )}
            {stock <= 0 && (
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-r from-red-700 to-red-900 text-white text-xs font-bold px-3 py-1.5 rounded-full"
              >
                STOKTA YOK
              </motion.span>
            )}
          </div>
        </div>
        
        {/* İçerik */}
        <div className="p-4 md:p-6">
          {/* Başlık ve Marka */}
          <div className="mb-3 md:mb-4">
            <h3 className="font-bold text-white text-lg md:text-xl mb-2 line-clamp-1 group-hover:text-blue-400 transition-colors">
              {guitar.name}
            </h3>
            <div className="flex items-center justify-between mb-2">
              {guitar.brand && (
                <span className="px-2 md:px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full border border-blue-500/30">
                  {guitar.brand}
                </span>
              )}
            </div>
            
            {/* Kategori */}
            {guitar.category && (
              <div className="mb-2">
                <span className="px-2 py-1 bg-gray-700/50 text-gray-300 text-sm rounded-full">
                  {guitar.category}
                </span>
              </div>
            )}
            
            {/* Kısa Açıklama */}
            {guitar.shortDescription && (
              <p className="text-gray-400 text-sm mb-3 md:mb-4 line-clamp-2 min-h-[40px] hidden md:block">
                {guitar.shortDescription}
              </p>
            )}
            
            {/* Etiketler */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 md:gap-2 mb-3 md:mb-4">
                {tags.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gradient-to-r from-purple-500/10 to-purple-600/10 text-purple-400 text-xs rounded-full border border-purple-500/20"
                  >
                    <FaTags size={10} className="inline mr-1" />
                    {tag}
                  </span>
                ))}
                {tags.length > 2 && (
                  <span className="px-2 py-1 bg-gray-800/50 text-gray-400 text-xs rounded-full">
                    +{tags.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Fiyat ve Stok */}
          <div className="flex items-center justify-between pt-3 md:pt-4 border-t border-gray-700/50">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl md:text-2xl font-bold text-white">
                  {guitar.price?.toLocaleString('tr-TR')} ₺
                </span>
                {discount > 0 && (
                  <span className="text-sm text-red-400 font-semibold">
                    (%{discount})
                  </span>
                )}
              </div>
              {guitar.originalPrice && discount > 0 && (
                <div className="text-gray-500 text-sm line-through">
                  {guitar.originalPrice.toLocaleString('tr-TR')} ₺
                </div>
              )}
              <div className={`text-sm mt-1 ${stock > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stock > 0 ? `Stok: ${stock} adet` : 'Tükendi'}
              </div>
            </div>

            {/* İncele Butonu */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/gitar/${guitar.id}`);
              }}
              className="px-3 md:px-4 py-2 md:py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all flex items-center gap-2 shadow-md hover:shadow-lg text-sm md:text-base"
            >
              <FaEye />
              <span className="hidden md:inline">İncele</span>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Filtre Sidebar Component (Mobil için overlay)
const FilterSidebar = ({ 
  filters, 
  onFilterChange, 
  categories, 
  tags,
  activeFiltersCount,
  isMobile,
  onClose 
}) => {
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    tags: true,
    price: true,
    status: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const content = (
    <div className={`${isMobile ? 'h-full overflow-y-auto' : ''}`}>
      <div className={`${isMobile ? '' : 'bg-gradient-to-b from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-4 md:p-6'}`}>
        {isMobile && (
          <div className="flex items-center justify-between mb-6 sticky top-0 bg-gray-900 py-4 z-10">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <FaArrowLeft className="text-white" />
              </button>
              <h3 className="text-xl font-bold text-white">Filtreler</h3>
            </div>
            {activeFiltersCount > 0 && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                {activeFiltersCount} aktif
              </span>
            )}
          </div>
        )}

        {!isMobile && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FaFilter className="text-blue-400" />
              <h3 className="text-xl font-bold text-white">Filtreler</h3>
            </div>
            {activeFiltersCount > 0 && (
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                {activeFiltersCount} aktif
              </span>
            )}
          </div>
        )}

        <div className="space-y-4 md:space-y-6">
          {/* Arama */}
          <div>
            <label className="block text-gray-300 text-sm mb-2">Arama</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Gitar ara..."
                value={filters.search}
                onChange={(e) => onFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
              />
            </div>
          </div>

          {/* Kategori */}
          {categories.length > 0 && (
            <div className="border-b border-gray-700/50 pb-4">
              <button
                onClick={() => toggleSection('category')}
                className="flex items-center justify-between w-full text-left mb-3"
              >
                <h4 className="text-gray-300 font-semibold">Kategori</h4>
                {expandedSections.category ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
              </button>
              <AnimatePresence>
                {expandedSections.category && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-2 max-h-48 overflow-y-auto pr-2"
                  >
                    <label className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                      <input
                        type="radio"
                        name="category"
                        checked={filters.category === "all"}
                        onChange={() => onFilterChange('category', "all")}
                        className="w-4 h-4 border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500/30"
                      />
                      <span className="text-gray-400 group-hover:text-white transition-colors text-sm">
                        Tüm Kategoriler
                      </span>
                    </label>
                    {categories.map(category => (
                      <label key={category} className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                        <input
                          type="radio"
                          name="category"
                          checked={filters.category === category}
                          onChange={() => onFilterChange('category', category)}
                          className="w-4 h-4 border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500/30"
                        />
                        <span className="text-gray-400 group-hover:text-white transition-colors text-sm">
                          {category}
                        </span>
                      </label>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Etiketler */}
          {tags.length > 0 && (
            <div className="border-b border-gray-700/50 pb-4">
              <button
                onClick={() => toggleSection('tags')}
                className="flex items-center justify-between w-full text-left mb-3"
              >
                <div className="flex items-center gap-2">
                  <h4 className="text-gray-300 font-semibold">Etiketler</h4>
                  {filters.tags.length > 0 && (
                    <span className="text-xs text-blue-400">({filters.tags.length} seçili)</span>
                  )}
                </div>
                {expandedSections.tags ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
              </button>
              <AnimatePresence>
                {expandedSections.tags && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-2 max-h-48 overflow-y-auto pr-2"
                  >
                    {tags.map(tag => (
                      <label key={tag} className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                        <input
                          type="checkbox"
                          checked={filters.tags.includes(tag)}
                          onChange={(e) => {
                            const newTags = e.target.checked
                              ? [...filters.tags, tag]
                              : filters.tags.filter(t => t !== tag);
                            onFilterChange('tags', newTags);
                          }}
                          className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500/30"
                        />
                        <span className="text-gray-400 group-hover:text-white transition-colors text-sm flex-1">
                          {tag}
                        </span>
                      </label>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Fiyat Aralığı */}
          <div className="border-b border-gray-700/50 pb-4">
            <button
              onClick={() => toggleSection('price')}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <h4 className="text-gray-300 font-semibold">Fiyat Aralığı</h4>
              {expandedSections.price ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
            </button>
            <AnimatePresence>
              {expandedSections.price && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">0 ₺</span>
                    <span className="text-sm text-gray-400">{filters.maxPrice.toLocaleString('tr-TR')} ₺</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="1000"
                    value={filters.maxPrice}
                    onChange={(e) => onFilterChange('maxPrice', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-800/50 rounded-lg">
                      <span className="text-blue-400 font-medium">
                        0 ₺ - {filters.maxPrice.toLocaleString('tr-TR')} ₺
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Özel Filtreler */}
          <div className="border-b border-gray-700/50 pb-4">
            <button
              onClick={() => toggleSection('status')}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <h4 className="text-gray-300 font-semibold">Durum</h4>
              {expandedSections.status ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
            </button>
            <AnimatePresence>
              {expandedSections.status && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-3"
                >
                  <label className="flex items-center justify-between cursor-pointer p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={filters.inStock}
                        onChange={(e) => onFilterChange('inStock', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500/30"
                      />
                      <span className="text-gray-400 hover:text-white transition-colors text-sm">
                        Sadece Stokta Olanlar
                      </span>
                    </div>
                    {filters.inStock && (
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    )}
                  </label>
                  
                  <label className="flex items-center justify-between cursor-pointer p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={filters.featured}
                        onChange={(e) => onFilterChange('featured', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500/30"
                      />
                      <span className="text-gray-400 hover:text-white transition-colors text-sm">
                        Sadece Öne Çıkanlar
                      </span>
                    </div>
                    {filters.featured && (
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    )}
                  </label>
                  
                  <label className="flex items-center justify-between cursor-pointer p-2 hover:bg-gray-700/30 rounded-lg transition-colors">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={filters.discounted}
                        onChange={(e) => onFilterChange('discounted', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500/30"
                      />
                      <span className="text-gray-400 hover:text-white transition-colors text-sm">
                        Sadece İndirimdekiler
                      </span>
                    </div>
                    {filters.discounted && (
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    )}
                  </label>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Filtreleri Temizle */}
          {activeFiltersCount > 0 && (
            <button
              onClick={() => onFilterChange('reset', null)}
              className="w-full py-3 bg-gradient-to-r from-red-500/20 to-red-600/10 text-gray-300 rounded-xl hover:from-red-600/30 hover:to-red-500/20 hover:text-white transition-all duration-300 border border-red-500/30 text-sm font-medium"
            >
              Tüm Filtreleri Temizle
            </button>
          )}

          {isMobile && (
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 text-sm font-medium mt-4"
            >
              Filtreleri Uygula ({activeFiltersCount})
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm"
      >
        <div className="h-full">
          {content}
        </div>
      </motion.div>
    );
  }

  return content;
};

// Mobil Filtre Butonu
const MobileFilterButton = ({ activeFiltersCount, onClick }) => (
  <button
    onClick={onClick}
    className="lg:hidden fixed bottom-6 right-6 z-40 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
    style={{ width: '56px', height: '56px' }}
  >
    <FaFilter size={20} />
    {activeFiltersCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
        {activeFiltersCount}
      </span>
    )}
  </button>
);

// Gitarlar sayfası
function Gitarlar() {
  const [guitars, setGuitars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    tags: [],
    maxPrice: 100000,
    inStock: false,
    featured: false,
    discounted: false
  });
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Mobil kontrolü
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Gitarları yükle
  useEffect(() => {
    const q = query(collection(db, "guitars"), orderBy("createdAt", "desc"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const guitarsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Kategorileri ve etiketleri çıkar
      const uniqueCategories = [...new Set(guitarsData.map(g => g.category).filter(Boolean))];
      const allTags = guitarsData.flatMap(g => g.tags || []).filter(Boolean);
      const uniqueTags = [...new Set(allTags)];
      
      setCategories(uniqueCategories);
      setTags(uniqueTags);
      setGuitars(guitarsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filtreleme fonksiyonu
  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({
        search: "",
        category: "all",
        tags: [],
        maxPrice: 100000,
        inStock: false,
        featured: false,
        discounted: false
      });
      return;
    }
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Aktif filtre sayısı
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.category !== "all") count++;
    if (filters.tags.length > 0) count++;
    if (filters.maxPrice < 100000) count++;
    if (filters.inStock) count++;
    if (filters.featured) count++;
    if (filters.discounted) count++;
    return count;
  }, [filters]);

  // Filtrelenmiş ve sıralanmış gitarlar
  const filteredGuitars = useMemo(() => {
    return guitars
      .filter(guitar => {
        // Arama filtresi
        const matchesSearch = !filters.search || 
          guitar.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
          guitar.brand?.toLowerCase().includes(filters.search.toLowerCase()) ||
          guitar.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
          guitar.shortDescription?.toLowerCase().includes(filters.search.toLowerCase()) ||
          (guitar.tags && guitar.tags.some(tag => 
            tag.toLowerCase().includes(filters.search.toLowerCase())
          ));
        
        // Kategori filtresi
        const matchesCategory = filters.category === "all" || guitar.category === filters.category;
        
        // Etiket filtresi
        const matchesTags = filters.tags.length === 0 || 
          (guitar.tags && filters.tags.every(tag => guitar.tags.includes(tag)));
        
        // Fiyat filtresi
        const price = parseFloat(guitar.price) || 0;
        const matchesPrice = price <= filters.maxPrice;
        
        // Stok filtresi
        const matchesStock = !filters.inStock || (guitar.stock > 0);
        
        // Öne çıkan filtresi
        const matchesFeatured = !filters.featured || guitar.featured;
        
        // İndirim filtresi
        const matchesDiscounted = !filters.discounted || calculateDiscount(guitar) > 0;
        
        return matchesSearch && matchesCategory && matchesTags && 
               matchesPrice && matchesStock && matchesFeatured && matchesDiscounted;
      })
      .sort((a, b) => {
        let aValue = a[sortBy] || "";
        let bValue = b[sortBy] || "";
        
        if (sortBy === "createdAt" || sortBy === "updatedAt") {
          aValue = aValue?.seconds || 0;
          bValue = bValue?.seconds || 0;
        } else if (sortBy === "price") {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (sortBy === "discount") {
          aValue = calculateDiscount(a);
          bValue = calculateDiscount(b);
        }
        
        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
  }, [guitars, filters, sortBy, sortOrder]);

  const totalGuitars = guitars.length;
  const filteredGuitarsCount = filteredGuitars.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
          <div className="text-gray-400">Gitarlar yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950">
      <AnimatePresence>
        {showMobileFilters && (
          <FilterSidebar
            filters={filters}
            onFilterChange={handleFilterChange}
            categories={categories}
            tags={tags}
            activeFiltersCount={activeFiltersCount}
            isMobile={true}
            onClose={() => setShowMobileFilters(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobil Filtre Butonu */}
      <MobileFilterButton 
        activeFiltersCount={activeFiltersCount}
        onClick={() => setShowMobileFilters(true)}
      />

      {/* Hero Section */}
      <div className="relative py-6 md:py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6 md:mb-8"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Gitar Koleksiyonu
            </h1>
            <p className="text-gray-400 text-sm md:text-base">
              {totalGuitars} gitar arasından keşfedin
            </p>
          </motion.div>

          {/* Ana İçerik */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Sol: Filtreler (Desktop) */}
            <div className="hidden lg:block lg:col-span-1">
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                categories={categories}
                tags={tags}
                activeFiltersCount={activeFiltersCount}
                isMobile={false}
              />
            </div>

            {/* Sağ: Gitarlar */}
            <div className="lg:col-span-3">
              {/* Sıralama Bar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-4 md:p-6 mb-6"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="text-xl md:text-2xl font-bold text-white">
                      {filteredGuitarsCount} Gitar
                    </div>
                    <p className="text-gray-400 text-sm">
                      {totalGuitars} gitardan {filteredGuitarsCount} tanesi gösteriliyor
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-gray-400 flex items-center gap-2 text-sm md:text-base">
                      <FaSortAmountDown />
                      <span className="hidden md:inline">Sırala:</span>
                    </span>
                    <select
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => {
                        const [field, order] = e.target.value.split('-');
                        setSortBy(field);
                        setSortOrder(order);
                      }}
                      className="bg-gray-900/50 border border-gray-700/50 rounded-xl px-3 md:px-4 py-2 md:py-3 text-white focus:outline-none focus:border-blue-500/50 min-w-[140px] md:min-w-[180px] text-sm md:text-base"
                    >
                      <option value="createdAt-desc">En Yeniler</option>
                      <option value="createdAt-asc">En Eski</option>
                      <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                      <option value="price-desc">Fiyat: Yüksekten Düşüğe</option>
                      <option value="discount-desc">En Çok İndirim</option>
                      <option value="name-asc">İsim: A-Z</option>
                      <option value="name-desc">İsim: Z-A</option>
                    </select>
                  </div>
                </div>

                {/* Aktif Filtreler */}
                {activeFiltersCount > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-700/50">
                    <div className="flex flex-wrap gap-2">
                      {filters.search && (
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30 flex items-center gap-1 md:gap-2">
                          "{filters.search}"
                          <button onClick={() => handleFilterChange('search', "")}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {filters.category !== "all" && (
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30 flex items-center gap-1 md:gap-2">
                          {filters.category}
                          <button onClick={() => handleFilterChange('category', "all")}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {filters.tags.map(tag => (
                        <span key={tag} className="px-2 md:px-3 py-1 md:py-1.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30 flex items-center gap-1 md:gap-2">
                          <FaTags size={10} />
                          {tag}
                          <button onClick={() => handleFilterChange('tags', filters.tags.filter(t => t !== tag))}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      ))}
                      {filters.maxPrice < 100000 && (
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30 flex items-center gap-1 md:gap-2">
                          Max: {filters.maxPrice.toLocaleString('tr-TR')} ₺
                          <button onClick={() => handleFilterChange('maxPrice', 100000)}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {filters.inStock && (
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30 flex items-center gap-1 md:gap-2">
                          Stokta
                          <button onClick={() => handleFilterChange('inStock', false)}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {filters.featured && (
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-amber-500/20 text-amber-400 text-xs rounded-full border border-amber-500/30 flex items-center gap-1 md:gap-2">
                          Öne Çıkan
                          <button onClick={() => handleFilterChange('featured', false)}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                      {filters.discounted && (
                        <span className="px-2 md:px-3 py-1 md:py-1.5 bg-red-500/20 text-red-400 text-xs rounded-full border border-red-500/30 flex items-center gap-1 md:gap-2">
                          İndirim
                          <button onClick={() => handleFilterChange('discounted', false)}>
                            <FaTimes size={10} />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Gitarlar Grid */}
              {filteredGuitars.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12 md:py-20"
                >
                  <FaSearch className="text-gray-600 text-4xl md:text-5xl mx-auto mb-4" />
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Gitar bulunamadı</h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto text-sm md:text-base">
                    Aradığınız kriterlere uygun gitar bulunamadı. Filtreleri temizleyerek tekrar deneyin.
                  </p>
                  <button
                    onClick={() => handleFilterChange('reset', null)}
                    className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-gray-500 text-white rounded-xl hover:from-blue-600 hover:to-gray-600 transition-all text-sm md:text-base"
                  >
                    <FaTimes />
                    Tüm Filtreleri Temizle
                  </button>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {filteredGuitars.map((guitar) => (
                    <GuitarCard key={guitar.id} guitar={guitar} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Gitarlar;